import { forwardRef } from "react";
import { Menu } from "../Menu";
import styles from "./PaginationItemsPerPageMenu.module.scss";
import IconKeyboardArrowDown from "@servicetitan/hammer-icon/mdi/round/keyboard_arrow_down.svg";
import { Button } from "../Button";
import { Text } from "../Text";
import { Flex } from "../Flex";

/**
 * Props for the PaginationItemsPerPageMenu component
 */
export type PaginationItemsPerPageMenuProps = {
  /**
   * The currently selected number of items per page.
   */
  itemsPerPage: number;
  /**
   * Array of available options for items per page.
   */
  itemsPerPageOptions: number[];
  /**
   * Callback function called when the items per page selection changes.
   * @param itemsPerPage The new number of items per page
   */
  onItemsPerPageChange: (itemsPerPage: number) => void;
};

/**
 * PaginationItemsPerPageMenu component for selecting the number of items per page.
 *
 * Features:
 * - Displays current items per page selection
 * - Provides dropdown menu with available options
 * - Integrates with pagination system
 * - Shows "Rows per page" label for clarity
 * - Fully accessible with proper ARIA attributes
 * - Consistent styling with design system
 *
 * @example
 * <PaginationItemsPerPageMenu
 *   itemsPerPage={10}
 *   itemsPerPageOptions={[5, 10, 25, 50]}
 *   onItemsPerPageChange={(itemsPerPage) => console.log('Items per page:', itemsPerPage)}
 * />
 */
export const PaginationItemsPerPageMenu = forwardRef<
  HTMLDivElement,
  PaginationItemsPerPageMenuProps
>((props, ref) => {
  const { itemsPerPage, itemsPerPageOptions, onItemsPerPageChange } = props;
  return (
    <Flex alignItems="center" gap="2" ref={ref}>
      <Text size="small">Rows per page</Text>
      <Menu
        trigger={({ ref, ...rest }) => {
          return (
            <Button
              ref={ref}
              {...rest}
              className={styles["pagination-items-per-page-button"]}
              appearance="ghost"
              icon={{ after: IconKeyboardArrowDown }}
              size="small"
            >
              {itemsPerPage}
            </Button>
          );
        }}
      >
        {itemsPerPageOptions.map((option) => (
          <Menu.Item
            key={option}
            label={option}
            onClick={() => {
              onItemsPerPageChange?.(option);
            }}
          />
        ))}
      </Menu>
    </Flex>
  );
});

PaginationItemsPerPageMenu.displayName = "PaginationItemsPerPageMenu";
